import React, { useState } from "react";
import { Check, ArrowRight, Puzzle, Sparkles, Award } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { GHL_LEAD_FLOW_URL, externalLinkProps } from "../config/ghl";
import { CTASection } from "../components/sections/CTASection";
import { FAQ } from "../components/sections/FAQ";
import { Band } from "../components/Band";
import { Seo } from "../components/Seo";
import { PoseEagle } from "../components/PoseEagle";

const PLANS_ORDER = ["pilot", "six", "twelve"];

// Plan pricing per package — package copy comes from t.packages.list (EN/NL).
const PACKAGES = [
  {
    key: "basic",
    plans: {
      pilot: { price: 249, bonus: null, avg: null },
      six: {
        price: 199,
        bonus: { en: "First month 50% off", nl: "Eerste maand 50% korting" },
        avg: { en: "approx. €182/month over 6 months", nl: "ca. €182/maand over 6 maanden" },
      },
      twelve: {
        price: 179,
        bonus: { en: "First 2 months 50% off + 1 optimization call", nl: "Eerste 2 maanden 50% korting + 1 optimalisatiegesprek" },
        avg: { en: "approx. €164/month over 12 months", nl: "ca. €164/maand over 12 maanden" },
      },
    },
  },
  {
    key: "pro",
    featured: true,
    plans: {
      pilot: { price: 349, bonus: null, avg: null },
      six: {
        price: 299,
        bonus: { en: "First 2 months 50% off + 1 optimization call", nl: "Eerste 2 maanden 50% korting + 1 optimalisatiegesprek" },
        avg: { en: "approx. €249/month over 6 months", nl: "ca. €249/maand over 6 maanden" },
      },
      twelve: {
        price: 269,
        bonus: { en: "First 3 months 50% off + 2 optimization calls", nl: "Eerste 3 maanden 50% korting + 2 optimalisatiegesprekken" },
        avg: { en: "approx. €235/month over 12 months", nl: "ca. €235/maand over 12 maanden" },
      },
    },
  },
  {
    key: "premium",
    plans: {
      pilot: { price: 549, bonus: null, avg: null },
      six: {
        price: 449,
        bonus: { en: "First 2 months 50% off + 1 optimization call", nl: "Eerste 2 maanden 50% korting + 1 optimalisatiegesprek" },
        avg: { en: "approx. €374/month over 6 months", nl: "ca. €374/maand over 6 maanden" },
      },
      twelve: {
        price: 399,
        bonus: { en: "First 4 months 50% off + 2 optimization calls + priority optimization", nl: "Eerste 4 maanden 50% korting + 2 optimalisatiegesprekken + prioriteitsoptimalisatie" },
        avg: { en: "approx. €333/month over 12 months", nl: "ca. €333/maand over 12 maanden" },
      },
    },
  },
];

const planLabels = {
  pilot: { en: "1 Month Pilot", nl: "1 maand pilot" },
  six: { en: "6 Month Plan", nl: "6 maanden" },
  twelve: { en: "12 Month Plan", nl: "12 maanden" },
};

const bestValueLabel = { en: "BEST LONG-TERM VALUE", nl: "BESTE LANGE-TERMIJN WAARDE" };

export default function PricingPage() {
  const { t, lang } = useI18n();
  const [plan, setPlan] = useState("twelve"); // default to best long-term value
  const [nodTick, setNodTick] = useState(0);

  return (
    <main className="pt-40 lg:pt-36" data-testid="pricing-page">
      <Seo k="pricing" />
      <Band tone="dark" className="pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <PoseEagle
          src="/eagle-poses/eagle-pricing-analytical.png"
          size={195}
          mobileSize={95}
          nod={nodTick}
          testid="pricing-eagle"
          className="flex justify-end -mb-6 md:mb-0 md:absolute md:right-6 lg:right-12 md:top-2"
        />
        <div className="max-w-2xl mb-8">
          <div className="eyebrow mb-3">{t.packages.eyebrow}</div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white tracking-tighter">{t.pricingPage.title}</h1>
          <p className="text-slate-400 mt-4 text-lg">{t.pricingPage.sub}</p>
          <div className="mt-5 glass rounded-xl p-4 flex gap-3 items-start max-w-2xl" data-testid="longterm-explainer">
            <div className="w-8 h-8 rounded-lg bg-cyan-500/15 border border-cyan-400/25 text-cyan-300 flex items-center justify-center shrink-0"><Sparkles size={14} /></div>
            <p className="text-slate-300 text-sm leading-relaxed">
              {lang === "nl"
                ? "Langere pakketten stellen ons in staat betere onboarding, optimalisatie en prijzen te bieden."
                : "Longer plans allow us to provide better onboarding, optimization, and pricing conditions."}
            </p>
          </div>
        </div>

        {/* Plan selector */}
        <div className="inline-flex bg-white/5 border border-white/10 rounded-xl p-1 mb-8 flex-wrap gap-1" role="tablist">
          {PLANS_ORDER.map((k) => (
            <button
              key={k}
              onClick={() => { if (plan !== k) setNodTick((n) => n + 1); setPlan(k); }}
              data-testid={`plan-${k}`}
              className={`px-4 py-2 text-sm rounded-lg font-medium transition-colors inline-flex items-center gap-2 ${plan === k ? "bg-blue-600 text-white shadow-[0_0_20px_rgba(37,99,235,0.35)]" : "text-slate-300 hover:text-white"}`}
            >
              {planLabels[k][lang] || planLabels[k].en}
              {k === "twelve" && (
                <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${plan === k ? "bg-white/15" : "bg-cyan-500/15 text-cyan-300"}`}>
                  {bestValueLabel[lang] || bestValueLabel.en}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Package cards */}
        <div className="grid md:grid-cols-3 gap-4 items-stretch">
          {PACKAGES.map((p, i) => (
            <PackageCard key={p.key} pkg={p} info={t.packages.list[i]} plan={plan} lang={lang} t={t} />
          ))}
        </div>

        {/* Notes */}
        <div className="mt-8 max-w-4xl mx-auto space-y-4">
          <div className="glass rounded-xl p-5 flex gap-4 items-start" data-testid="packages-note">
            <div className="w-8 h-8 rounded-lg bg-cyan-500/15 border border-cyan-400/25 text-cyan-300 flex items-center justify-center shrink-0 font-mono text-sm font-bold">i</div>
            <p className="text-slate-300 text-sm leading-relaxed">{t.packages.note}</p>
          </div>
          <p className="text-center text-xs text-slate-500">
            {lang === "nl"
              ? "Prijzen worden weergegeven in EUR en zijn exclusief BTW. TP Agency richt zich momenteel op Nederlandse en Europese bedrijven."
              : "Prices are shown in EUR and exclude VAT. TP Agency currently focuses on Dutch and European businesses."}
          </p>
        </div>
        </div>
      </Band>

      {/* Add-ons */}
      <Band tone="light">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <section className="pt-14 pb-16" data-testid="addons-section">
          <div className="max-w-2xl mb-8">
            <div className="eyebrow mb-3">{t.pricingPage.addOnsTitle}</div>
            <h2 className="font-display font-bold text-2xl sm:text-3xl text-white tracking-tight">{t.pricingPage.addOnsSub}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {t.pricingPage.addOns.map((a, i) => (
              <div key={a.title} className="glass rounded-xl p-6" data-testid={`addon-${i}`}>
                <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 text-cyan-300 flex items-center justify-center mb-3">
                  <Puzzle size={15} />
                </div>
                <div className="font-semibold text-white">{a.title}</div>
                <p className="text-slate-400 text-sm mt-1.5 leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-sm text-slate-400 max-w-3xl mx-auto italic">{t.pricingPage.adjustNote}</p>
        </section>
        </div>
      </Band>
      <Band tone="dark"><FAQ /></Band>
      <Band tone="light"><CTASection /></Band>
    </main>
  );
}

const PackageCard = ({ pkg, info, plan, lang, t }) => {
  const featured = !!pkg.featured;
  const active = pkg.plans[plan];
  return (
    <div
      className={`relative rounded-2xl p-7 sm:p-8 flex flex-col ${featured ? "bg-gradient-to-b from-blue-600/15 to-cyan-500/5 border border-cyan-400/30 shadow-[0_0_40px_rgba(37,99,235,0.25)]" : "glass"}`}
      data-testid={`package-${pkg.key}`}
    >
      {featured && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-mono tracking-widest font-bold uppercase bg-cyan-400 text-[#060B19]">
          {info.badge}
        </span>
      )}
      <div className="text-cyan-300 font-mono text-xs tracking-[0.18em] uppercase font-bold min-h-[36px] flex items-center leading-snug">{info.name}</div>

      <div className="mt-4 flex items-end gap-1.5">
        <div className="font-display font-extrabold text-white text-5xl leading-none">€{active.price}</div>
        <div className="text-slate-400 text-sm pb-1.5">/ {lang === "nl" ? "maand" : "month"}</div>
      </div>
      <div className="text-[11px] text-slate-500 mt-1">
        {planLabels[plan][lang] || planLabels[plan].en} · {t.packages.vat}
      </div>

      {active.bonus && (
        <div className="mt-4 rounded-xl border border-cyan-400/25 bg-cyan-500/5 px-4 py-3" data-testid={`bonus-${pkg.key}`}>
          <div className="flex items-center gap-1.5 text-[10px] font-bold tracking-widest text-cyan-300 mb-1">
            <Award size={12} /> {lang === "nl" ? "LANGE-TERMIJN BONUS" : "LONG-TERM PLAN BONUS"}
          </div>
          <p className="text-slate-200 text-sm leading-snug">{active.bonus[lang] || active.bonus.en}</p>
          {active.avg && (
            <p className="text-[11px] text-slate-400 mt-1.5 italic" data-testid={`avg-${pkg.key}`}>
              {lang === "nl" ? `Effectief gemiddeld: ${active.avg.nl}` : `Effective average: ${active.avg.en}`}
            </p>
          )}
        </div>
      )}

      <div className="mt-5">
        <div className="text-[10px] font-bold tracking-widest uppercase text-slate-400 mb-1.5">{lang === "nl" ? "Ideaal voor" : "Best for"}</div>
        <p className="text-slate-200 text-sm leading-relaxed">{info.bestFor}</p>
      </div>

      <p className="text-slate-400 text-sm mt-4 leading-relaxed italic">{info.desc}</p>

      <div className="mt-6">
        <div className="text-[10px] font-bold tracking-widest uppercase text-slate-400 mb-3">{lang === "nl" ? "Inclusief" : "Includes"}</div>
        <ul className="space-y-2.5 flex-1">
          {info.features.map((f) => (
            <li key={f} className="flex items-start gap-2.5 text-sm text-slate-200">
              <Check size={16} className={`mt-0.5 shrink-0 ${featured ? "text-cyan-300" : "text-blue-400"}`} />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex-1" />
      <a
        href={GHL_LEAD_FLOW_URL}
        {...externalLinkProps}
        data-testid={`package-cta-${pkg.key}`}
        className={`mt-8 inline-flex items-center justify-center gap-2 w-full py-3 rounded-lg text-sm font-semibold ${featured ? "btn-cta" : "bg-white/5 hover:bg-white/10 border border-white/15 text-white"}`}
      >
        {t.nav.bookDemo} <ArrowRight size={14} />
      </a>
    </div>
  );
};
